import { useEffect, useRef } from 'react'
import { X, Minimize2, Loader2, CheckCircle, XCircle, Bot, Play } from 'lucide-react'
import { useBackgroundTaskStore, type BackgroundTask, type TaskStatus } from '../stores/backgroundTaskStore'

export function BackgroundTaskModal() {
  const { tasks, activeModalTaskId, minimizeTask, removeTask, setActiveModal } = useBackgroundTaskStore()
  const outputRef = useRef<HTMLDivElement>(null)
  
  const task = activeModalTaskId ? tasks.get(activeModalTaskId) : null
  
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight
    }
  }, [task?.events.length])

  if (!task) return null

  const isRunning = task.status === 'running'
  const config = getTaskConfig(task)

  const handleMinimize = () => {
    minimizeTask(task.id)
  }

  const handleClose = () => {
    if (isRunning) {
      // Just minimize if running
      minimizeTask(task.id)
    } else {
      // Remove completed tasks
      removeTask(task.id)
    }
  }

  const handleBackdropClick = () => {
    if (isRunning) {
      minimizeTask(task.id)
    } else {
      setActiveModal(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />

      {/* Slideover Panel */}
      <div className="relative w-full max-w-xl bg-[#1a1a1a] border-l border-white/10 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${config.bgColor} rounded`}>
              {task.type === 'scan' ? (
                <Bot size={18} className={config.color} />
              ) : (
                <Play size={18} className={config.color} />
              )}
            </div>
            <div>
              <h3 className="font-medium text-white">{task.title}</h3>
              {task.subtitle && (
                <p className="text-xs text-white/50 truncate max-w-[280px]">{task.subtitle}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <StatusBadge status={task.status} config={config} />
            <button
              onClick={handleMinimize}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title="Minimize to background"
            >
              <Minimize2 size={18} className="text-white/50" />
            </button>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title={isRunning ? 'Minimize' : 'Close'}
            >
              <X size={18} className="text-white/50" />
            </button>
          </div>
        </div>

        {/* Output */}
        <div
          ref={outputRef}
          className="flex-1 overflow-y-auto p-4 font-mono text-sm bg-black/30"
        >
          {task.events.length === 0 ? (
            <div className="flex items-center gap-2 text-white/40">
              <Loader2 size={14} className="animate-spin" />
              <span>Starting {task.type}...</span>
            </div>
          ) : (
            task.events.map((event, i) => (
              <div key={i} className={`flex gap-2 py-0.5 ${getEventColor(event.type)}`}>
                <span className="text-white/30 select-none shrink-0">{getEventPrefix(event.type)}</span>
                <span className="whitespace-pre-wrap break-words">{event.content}</span>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-white/10 shrink-0">
          <div className="text-xs text-white/40">
            {task.events.length} event{task.events.length !== 1 ? 's' : ''}
            {task.metadata?.workflowId && (
              <span className="ml-2 text-emerald-400">• Workflow #{task.metadata.workflowId}</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleMinimize}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-sm rounded transition-colors flex items-center gap-1.5"
            >
              <Minimize2 size={14} />
              {isRunning ? 'Run in Background' : 'Minimize'}
            </button>
            <button
              onClick={handleClose}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-sm rounded transition-colors"
            >
              {isRunning ? 'Cancel' : 'Close'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.2s ease-out;
        }
      `}</style>
    </div>
  )
}

function getTaskConfig(task: BackgroundTask) {
  if (task.type === 'scan') {
    return {
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/20',
    }
  }
  
  // Execution - use executor color if available
  const executor = task.metadata?.executor
  switch (executor) {
    case 'gemini':
      return { color: 'text-blue-400', bgColor: 'bg-blue-500/20' }
    case 'codex':
      return { color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' }
    default:
      return { color: 'text-orange-400', bgColor: 'bg-orange-500/20' }
  }
}

function StatusBadge({ status, config }: { status: TaskStatus; config: { color: string; bgColor: string } }) {
  switch (status) {
    case 'running':
      return (
        <div className={`flex items-center gap-1.5 px-2 py-1 ${config.bgColor} rounded ${config.color} text-xs`}>
          <Loader2 size={12} className="animate-spin" />
          Running
        </div>
      )
    case 'success':
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-500/20 rounded text-emerald-400 text-xs">
          <CheckCircle size={12} />
          Complete
        </div>
      )
    case 'error':
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-red-500/20 rounded text-red-400 text-xs">
          <XCircle size={12} />
          Failed
        </div>
      )
  }
}

function getEventColor(type: string): string {
  switch (type) {
    case 'init':
    case 'start':
      return 'text-blue-400'
    case 'progress':
      return 'text-cyan-400'
    case 'tool_use':
      return 'text-amber-400'
    case 'error':
      return 'text-red-400'
    case 'complete':
    case 'workflow_created':
      return 'text-emerald-400'
    default:
      return 'text-white/70'
  }
}

function getEventPrefix(type: string): string {
  switch (type) {
    case 'init':
    case 'start':
      return '▶'
    case 'progress':
      return '◆'
    case 'tool_use':
      return '🔧'
    case 'error':
      return '✗'
    case 'complete':
      return '✓'
    case 'workflow_created':
      return '📋'
    default:
      return '│'
  }
}
